import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import React, { useEffect, useRef, useState } from "react";

/** Shows the full text in a tooltip only when the label is truncated (CSS ellipsis). */
export const TruncatedTextTooltip: React.FC<{
  children: React.ReactNode;
  content: React.ReactNode;
}> = ({ children, content }) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const isTextTruncated = textRef.current.scrollWidth > textRef.current.clientWidth;
        setIsTruncated(isTextTruncated);
      }
    };

    checkTruncation();
    window.addEventListener("resize", checkTruncation);

    const resizeObserver = new ResizeObserver(checkTruncation);
    if (textRef.current) {
      resizeObserver.observe(textRef.current);
    }

    return () => {
      window.removeEventListener("resize", checkTruncation);
      resizeObserver.disconnect();
    };
  }, [children]);

  if (!isTruncated) {
    /* istanbul ignore next: UI-only markup */
    return <p ref={textRef} className="truncate">{children}</p>;
  }

  /* istanbul ignore next: UI-only markup */
  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <p ref={textRef} className="truncate">
            {children}
          </p>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="max-w-xs text-xs">{content}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
